import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import ChatInterface from './components/ChatInterface';
import SignInOverlay from './components/SignInOverlay';
import LoadingScreen from './components/LoadingScreen';
import AuthAction from './components/AuthAction';
import { ThemeProvider } from './context/ThemeContext';
import { 
  onAuthStateChange, 
  signOutUser, 
  completePasswordlessSignIn,
  isSignInLink 
} from './firebase/authService';
import analyticsService from './utils/analyticsService';
import { downloadConversationAsPdf } from './utils/pdfService';

// Unified route component that handles both home and conversation routes
const AppContent = ({
  messages,
  setMessages,
  currentConversationId,
  setCurrentConversationId,
  conversations,
  loadConversation,
  startNewConversation,
  refreshConversations,
  deleteConversation,
  isAuthenticated,
  scrollToBottomTrigger,
  user,
  downloadConversation,
  handleSignOut,
  handleShowSignIn,
  showSignIn,
  showSignOut,
  handleCloseSignIn,
  handleCloseSignOut,
  handleSignIn,
  handleConfirmSignOut,
  isNavigatingRef
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract conversation ID from URL path
  const urlConversationId = React.useMemo(() => {
    const match = location.pathname.match(/^\/ch\/([^/]+)$/);
    return match ? match[1] : null;
  }, [location.pathname]);
  
  const isInitialMount = React.useRef(true);
  
  // Determine initial loading state based on URL - BEFORE first render!
  const [isLoadingConversation, setIsLoadingConversation] = React.useState(() => {
    // If URL has a conversation ID on mount and user is authenticated, we should show loading
    return !!urlConversationId && isAuthenticated;
  });
  
  const currentConversationIdRef = React.useRef(currentConversationId); // Track current ID in ref
  
  // Update ref when state changes
  React.useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // Handle conversation loading from URL changes
  React.useEffect(() => {
    const loadFromUrl = async () => {
      // If URL has no conversation ID, we're on home - clear state
      if (!urlConversationId) {
        // Only clear if we actually have a conversation loaded
        if (currentConversationIdRef.current) {
          setMessages([]);
          setCurrentConversationId(null);
        }
        isInitialMount.current = false;
        return;
      }

      // If not authenticated, redirect to home
      if (!isAuthenticated) {
        navigate('/', { replace: true });
        handleShowSignIn();
        isInitialMount.current = false;
        return;
      }

      // If URL conversation is different from current, load it
      if (urlConversationId !== currentConversationIdRef.current) {
        // Skip if we just initiated this navigation ourselves
        if (isNavigatingRef.current) {
          isNavigatingRef.current = false;
          isInitialMount.current = false;
          return;
        }
        
        // Only show loading state on initial mount (page reload)
        // Not when navigating between conversations
        const shouldShowLoading = isInitialMount.current;
        
        if (shouldShowLoading) {
          setIsLoadingConversation(true);
        }
        
        try {
          // Load without navigate to avoid URL update (already correct)
          await loadConversation(urlConversationId, null);
        } catch (err) {
          console.error('Error loading conversation from URL:', err);
          // On error, go back to home
          navigate('/', { replace: true });
        } finally {
          if (shouldShowLoading) {
            setIsLoadingConversation(false);
          }
        }
      }
      
      isInitialMount.current = false;
    };

    loadFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlConversationId, isAuthenticated]); // Removed currentConversationId to prevent re-trigger after state update

  // Show chat interface if we have messages OR if we're loading on initial mount
  const shouldShowChat = messages.length > 0 || isLoadingConversation;

  return (
    <>
      <Header 
        showDownloadButton={messages.length > 0}
        onDownloadConversation={downloadConversation}
        user={user}
        onSignOut={isAuthenticated ? handleSignOut : null}
        onSignIn={!isAuthenticated ? handleShowSignIn : null}
      />
      <ChatInterface 
        messages={messages}
        setMessages={setMessages}
        currentConversationId={currentConversationId}
        setCurrentConversationId={setCurrentConversationId}
        conversations={conversations}
        onLoadConversation={(convId) => loadConversation(convId, navigate)}
        onNewConversation={() => startNewConversation(navigate)}
        onRefreshConversations={refreshConversations}
        onDeleteConversation={deleteConversation}
        isAuthenticated={isAuthenticated}
        scrollToBottomTrigger={scrollToBottomTrigger}
        onShowSignIn={handleShowSignIn}
        navigate={navigate}
        isLoadingConversation={isLoadingConversation}
        shouldShowChat={shouldShowChat}
      />
      
      {/* Sign In Overlay */}
      <SignInOverlay
        isOpen={showSignIn}
        onClose={handleCloseSignIn}
        onSignIn={handleSignIn}
        mode="signin"
      />
      
      {/* Sign Out Overlay */}
      <SignInOverlay
        isOpen={showSignOut}
        onClose={handleCloseSignOut}
        mode="signout"
        onConfirmSignOut={handleConfirmSignOut}
      />
    </>
  );
};

function App() {
  const [messages, setMessages] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false); // Don't show initially
  const [showSignOut, setShowSignOut] = useState(false); // Sign out confirmation
  const [isAuthLoading, setIsAuthLoading] = useState(true); // Add loading state
  
  // Scroll trigger for chat interface
  const [scrollToBottomTrigger, setScrollToBottomTrigger] = useState(0);
  
  // Track if we initiated navigation to prevent URL effect from double-loading
  const isNavigatingRef = React.useRef(false);

  console.log('App render - showSignIn:', showSignIn, 'isAuthenticated:', isAuthenticated);

  // Firebase authentication state management
  useEffect(() => {
    // Check if the current URL contains a sign-in link
    if (isSignInLink()) {
      const handleEmailLinkSignIn = async () => {
        try {
          const user = await completePasswordlessSignIn(window.location.href);
          console.log('Successfully signed in with email link:', user.email);
          // Clean up the URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error completing email link sign-in:', error);
        }
      };
      
      handleEmailLinkSignIn();
    }

    // Set up auth state listener
    const unsubscribe = onAuthStateChange((user) => {
      setIsAuthLoading(false); // Auth check complete
      
      if (user) {
        // User is signed in
        setUser({
          email: user.email,
          uid: user.uid,
          emailVerified: user.emailVerified
        });
        setIsAuthenticated(true);
        setShowSignIn(false);
        console.log('User authenticated:', user.email);
      } else {
        // User is signed out
        setUser(null);
        setIsAuthenticated(false);
        // Don't automatically show sign-in overlay - it will be triggered by user action
        // setShowSignIn(true); // Removed automatic popup
        // Clear conversation data on sign out
        setMessages([]);
        setCurrentConversationId(null);
        setConversations([]);
        console.log('User signed out');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Analytics: Track session start and page view
  useEffect(() => {
    analyticsService.trackSessionStart();
    analyticsService.trackPageView('/', 'Home - AI Recruiter Chatbot');
  }, []);

  // Load conversations when user is authenticated
  useEffect(() => {
    const loadConversations = async () => {
      if (!isAuthenticated) return;
      
      try {
        // Import the authenticated API function dynamically to avoid import issues
        const { getAuthenticatedConversations } = await import('./utils/authApi');
        const data = await getAuthenticatedConversations();
        setConversations(data.conversations || []);
      } catch (error) {
        console.error('Failed to load conversations:', error);
      }
    };

    loadConversations();
  }, [isAuthenticated]);

  // Load specific conversation when selected
  const loadConversation = React.useCallback(async (conversationId, navigate) => {
    if (!isAuthenticated) return;
    
    // Set conversation ID FIRST to prevent race condition with URL sync effect
    setCurrentConversationId(conversationId);
    
    try {
      const { getAuthenticatedConversationDetail } = await import('./utils/authApi');
      const data = await getAuthenticatedConversationDetail(conversationId);
      const conversation = data.conversation;
      
      // Convert conversation messages to frontend format
      const formattedMessages = conversation.messages.map(msg => ({
        id: msg.message_id,
        text: msg.content,
        isUser: msg.role === 'user',
        timestamp: new Date(msg.timestamp).toISOString()
      }));
      
      setMessages(formattedMessages);
      
      // Update URL to reflect current conversation
      if (navigate) {
        // Set flag to tell URL effect we initiated this navigation
        isNavigatingRef.current = true;
        navigate(`/ch/${conversationId}`, { replace: true });
      }
      
      // Trigger scroll to bottom after loading conversation
      setTimeout(() => {
        setScrollToBottomTrigger(prev => prev + 1);
      }, 50);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      // Reset conversation ID on error
      setCurrentConversationId(null);
    }
  }, [isAuthenticated]);

  // Start new conversation
  const startNewConversation = (navigate) => {
    setMessages([]);
    setCurrentConversationId(null);
    
    // Update URL to go back to home
    if (navigate) {
      // Set flag to tell URL effect we initiated this navigation
      isNavigatingRef.current = true;
      navigate('/', { replace: true });
    }
  };

  // Update conversations list when a new conversation is created or updated
  const refreshConversations = async () => {
    if (!isAuthenticated) return;
    
    try {
      const { getAuthenticatedConversations } = await import('./utils/authApi');
      const data = await getAuthenticatedConversations();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to refresh conversations:', error);
    }
  };

  // Delete a conversation
  const deleteConversation = async (conversationId) => {
    if (!isAuthenticated) return;
    
    try {
      const { deleteAuthenticatedConversation } = await import('./utils/authApi');
      await deleteAuthenticatedConversation(conversationId);
      
      // Remove from conversations list
      setConversations(prev => prev.filter(conv => conv.conversation_id !== conversationId));
      
      // Clear current conversation if it was deleted
      if (currentConversationId === conversationId) {
        setMessages([]);
        setCurrentConversationId(null);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const downloadConversation = () => {
    downloadConversationAsPdf(messages);
  };

  // Authentication handlers
  const handleSignIn = (email) => {
    // This is now handled by Firebase auth state listener
    console.log('Sign-in initiated for:', email);
  };

  const handleSignOut = () => {
    // Show the sign-out confirmation overlay
    setShowSignOut(true);
  };

  const handleConfirmSignOut = async () => {
    try {
      await signOutUser();
      // State will be updated by the auth state listener
    } catch (error) {
      console.error('Error signing out:', error);
      throw error; // Re-throw to let SignInOverlay handle the error
    }
  };

  const handleCloseSignIn = () => {
    // For now, allow closing the sign-in modal for development
    // In production, this might not be allowed or have different behavior
    setShowSignIn(false);
  };

  const handleCloseSignOut = () => {
    setShowSignOut(false);
  };

  const handleShowSignIn = () => {
    setShowSignIn(true);
  };

  // Show loading screen during auth check
  if (isAuthLoading) {
    return (
      <ThemeProvider>
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Authentication action handler route */}
            <Route path="/auth/action" element={<AuthAction />} />
            
            {/* Main app - handles both / and /ch/:conversationId */}
            <Route path="/*" element={
              <AppContent
                messages={messages}
                setMessages={setMessages}
                currentConversationId={currentConversationId}
                setCurrentConversationId={setCurrentConversationId}
                conversations={conversations}
                loadConversation={loadConversation}
                startNewConversation={startNewConversation}
                refreshConversations={refreshConversations}
                deleteConversation={deleteConversation}
                isAuthenticated={isAuthenticated}
                scrollToBottomTrigger={scrollToBottomTrigger}
                user={user}
                downloadConversation={downloadConversation}
                handleSignOut={handleSignOut}
                handleShowSignIn={handleShowSignIn}
                showSignIn={showSignIn}
                showSignOut={showSignOut}
                handleCloseSignIn={handleCloseSignIn}
                handleCloseSignOut={handleCloseSignOut}
                handleSignIn={handleSignIn}
                handleConfirmSignOut={handleConfirmSignOut}
                isNavigatingRef={isNavigatingRef}
              />
            } />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;